import posthog from "posthog-js";

import { redactUrl } from "./redactUrl";
import { getTelemetryConfig } from "./telemetryConfig";
import type { ErrorContext, ErrorTracker } from "./types";

// This is the ONLY file in the repo allowed to import `posthog-js`.
// Errors only: autocapture, pageview/pageleave capture and session recording are all
// disabled — this is a medical app and URLs/DOM text can contain PHI.
export class PostHogErrorTracker implements ErrorTracker {
  init(): void {
    try {
      const { token, host } = getTelemetryConfig();
      posthog.init(token, {
        api_host: host,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        person_profiles: "identified_only",
        // Disabling pageview capture does NOT stop posthog-js attaching URL properties to every
        // event it sends. This is the last gate before anything leaves the browser.
        sanitize_properties: (properties) => ({
          ...properties,
          $current_url: redactUrl(properties.$current_url),
          $pathname: redactUrl(properties.$pathname),
          // The referrer is a full un-redactable URL from anywhere in the app — drop it.
          $referrer: undefined,
          $referring_domain: undefined,
        }),
      });
    } catch (err) {
      console.warn("[telemetry] failed to initialize PostHog", err);
    }
  }

  captureError(error: unknown, context?: ErrorContext): void {
    try {
      const normalized = toError(error);
      posthog.captureException(normalized, context);
    } catch (err) {
      console.warn("[telemetry] failed to capture error", err);
    }
  }

  identify(userId: string, traits?: ErrorContext): void {
    try {
      posthog.identify(userId, traits);
    } catch (err) {
      console.warn("[telemetry] failed to identify user", err);
    }
  }

  reset(): void {
    try {
      posthog.reset();
    } catch (err) {
      console.warn("[telemetry] failed to reset telemetry state", err);
    }
  }
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  try {
    return new Error(typeof error === "string" ? error : JSON.stringify(error));
  } catch {
    return new Error(String(error));
  }
}
